const numberRegex = /^[0-9]+$/;
const decimalRegex = /^[0-9]+(\.[0-9]{1,2})?$/;

const handleValidateLot = (retVal, lotNumber, invNumber, lotExt, deletedLots, lotNumberArray, type) => {
  if (type !== 'bin' && lotNumber?.length > 8) {
    retVal.lotNumber = 'Item number must be 8 characters or less.';
    return;
  }
  if (!numberRegex.test(lotNumber)) {
    retVal.lotNumber = 'Item number must contain numerical values only.';
    return;
  }
  if (deletedLots.has(lotNumber + lotExt.toUpperCase())) {
    retVal.lotNumber = 'Item number was previously deleted.';
    return;
  }
  if (lotNumberArray.some(lotNum => lotNum === (lotNumber + lotExt.toUpperCase()))) {
    retVal.lotNumber = 'Item number already in use.';
    return;
  }
};

export const validateLot = (deletedLots, lotNumber, lotExtension, lotNumberArray, invNumber, isGoldPlatinum, type = null) => {
  const retVal = {
    lotNumber: null,
    lotExt: null,
    invNumber: null
  };

  // Safeguard for the .has below
  let lotExt = '';
  if (lotExtension) {
    lotExt = lotExtension;
  }

  if (isGoldPlatinum) {
    if (!(lotNumber || invNumber)) {
      retVal.lotNumber = 'Item number or inventory number are required.';
      retVal.invNumber = 'Item number or inventory number are required.';
    }

    if (lotNumber) {
      handleValidateLot(retVal, lotNumber, invNumber, lotExt, deletedLots, lotNumberArray, type);
    }
  } else {
    if (!lotNumber) {
      retVal.lotNumber = 'Item number is required.';
    }

    handleValidateLot(retVal, lotNumber, invNumber, lotExt, deletedLots, lotNumberArray, type);
  }

  const letterRegex = /^[a-zA-Z]{1}$/;
  if (lotExt && !letterRegex.test(lotExt)) {
    retVal.lotExt = 'Item extension must be between A-Z.';
  }

  return retVal;
};

export const validateLotCatalog = (lotNumber, lotExtension, lotNumberArray, lots) => {
  const retVal = {
    lotNumber: null,
    lotExt: null
  };

  // Safeguard for the .has below
  let lotExt = '';
  if (lotExtension) {
    lotExt = lotExtension;
  }

  if (!lotNumber) {
    retVal.lotNumber = 'Item number is required.';
    return retVal;
  } if (!numberRegex.test(lotNumber)) {
    retVal.lotNumber = 'Item number must contain numerical values only.';
  } else if (lotNumberArray.some(lotNum => lotNum === lotNumber + lotExt.toUpperCase())) {
    retVal.lotNumber = 'Item number already in use.';
  } else if (!lots.some(lotNum => lotNum.lotNumber + lotNum.lotExt.toUpperCase()
                === lotNumber + lotExt.toUpperCase())) {
    retVal.lotNumber = 'This item number does not exist in this catalog.';
  }

  const letterRegex = /^[a-zA-Z]{1}$/;
  if (lotExt && !letterRegex.test(lotExt)) {
    retVal.lotExt = 'Item extension must be between A-Z.';
    return retVal;
  }

  return retVal;
};

export const validateLotTitle = lotTitle => {
  if (!lotTitle) {
    return 'Item title is required.';
  }

  if (lotTitle.length > 250) {
    return 'Item title must be 250 characters or less.';
  }

  return null;
};

export const validateLotDescription = lotDescription => {
  if (!lotDescription) {
    return 'Item description is required.';
  }

  return null;
};

export const validateShippingField = (label, value) => {
  if (value && !numberRegex.test(value)) {
    return `${label} must contain numerical values only.`;
  }

  return null;
};

// Need to validate loEst and hiEst together
export const validateEst = (loEst, hiEst) => {
  const retVal = {
    loEst: null,
    hiEst: null
  };

  // Note that the values come in as a string... this is needed for the proper > comparison
  const low = parseInt(loEst, 10);
  const high = parseInt(hiEst, 10);

  if (hiEst && hiEst.indexOf('.') >= 0) {
    retVal.hiEst = 'High estimate must be a whole number.';
  } else if (hiEst && !numberRegex.test(hiEst)) {
    retVal.hiEst = 'High estimate must contain numerical values only.';
  }

  if (loEst && loEst.indexOf('.') >= 0) {
    retVal.loEst = 'Low estimate must be a whole number.';
  } else if (loEst && !numberRegex.test(loEst)) {
    retVal.loEst = 'Low estimate must contain numerical values only.';
  } else if (low && high && high > 0 && low >= high) {
    retVal.loEst = 'Low estimate must be lower than high estimate.';
  }

  return retVal;
};

export const validateItemPrice = itemPrice => {
  if (!itemPrice) {
    return 'Price is required.';
  }

  if (itemPrice.indexOf('.') >= 0) {
    return 'Price must be a whole number.';
  }

  if (!numberRegex.test(itemPrice)) {
    return 'Price must contain numerical values only.';
  }

  if (itemPrice <= 0) {
    return 'Price must be greater than 0.';
  }

  return null;
};

export const validateRealizedPrice = realizedPrice => {
  if (!realizedPrice) {
    return 'Realized price is required. Please use zero for unsold item.';
  }

  if (!numberRegex.test(realizedPrice.replace('.', ''))) {
    return 'Must be a numerical value.';
  }

  if (!decimalRegex.test(realizedPrice)) {
    return 'Realized price must not have more than two digits after the decimal point.';
  }

  if (realizedPrice < 0) {
    return 'Realized Price must be greater or equal than 0.';
  }

  return null;
};

export const validateStartingBid = startingBid => {
  if (!startingBid) {
    return null;
  }

  if (startingBid.indexOf('.') >= 0) {
    return 'Starting bid must be a whole number.';
  }

  if (!numberRegex.test(startingBid)) {
    return 'Starting bid must contain numerical values only.';
  }

  return null;
};

export const validateInvNumber = (invNumber, invNumberArray, lotNumber) => {
  if (!lotNumber && !invNumber) {
    return 'Item number or inventory number are required.';
  }

  if (!invNumber) {
    return null;
  }

  // Alphanumeric and dashes
  const invRegex = /^[A-Za-z0-9-]+$/;
  if (invNumber.length > 32) {
    return 'Inventory number must be 32 characters or less.';
  }

  if (!invRegex.test(invNumber)) {
    return 'Inventory number must only contain alpha-numeric characters and dashes.';
  }

  if (invNumberArray.some(invNum => invNum === invNumber)) {
    return 'Inventory number already in use.';
  }

  return null;
};

export const validateReserve = reserve => {
  if (!reserve) {
    return null;
  }

  if (reserve.indexOf('.') >= 0) {
    return 'Reserve must be a whole number.';
  }

  if (!numberRegex.test(reserve)) {
    return 'Reserve must contain numerical values only.';
  }

  return null;
};
